import { Howl } from 'howler';

/**
 * ThemeSoundManager - Singleton class for managing theme-specific game sounds
 *
 * Provides theme-aware sound playback with seamless fallback to default sounds.
 * Lazy loads theme sounds when a 3D board theme is selected.
 */
class ThemeSoundManager {
  constructor(defaultSounds) {
    this.defaultSounds = defaultSounds;
    this.activeThemeId = null;
    this.themeSounds = {};
    this.currentThemeConfig = null;
    this.loadedThemeId = null;
  }

  /**
   * Set the active theme and load its sounds
   * @param {string} themeId - Theme ID ('inferno', 'frost', 'spectrum', or null)
   * @param {object} themeConfig - Theme sound configuration
   */
  setActiveTheme(themeId, themeConfig) {
    // If no theme or same theme, skip
    if (themeId === this.activeThemeId && this.loadedThemeId === themeId) {
      return;
    }

    // Unload previous theme sounds
    if (this.loadedThemeId !== null && this.loadedThemeId !== themeId) {
      this.unloadThemeSounds();
    }

    this.activeThemeId = themeId;

    // Load new theme sounds if available
    if (themeId && themeConfig) {
      this.loadThemeSounds(themeConfig);
    }
  }

  /**
   * Load theme sounds from configuration
   * @param {object} themeConfig - Theme sound configuration with sounds and mappings
   */
  loadThemeSounds(themeConfig) {
    if (!themeConfig || !themeConfig.sounds) {
      return;
    }

    this.currentThemeConfig = themeConfig;
    this.themeSounds = {};

    // Create Howl instances for each theme sound
    Object.entries(themeConfig.sounds).forEach(([key, soundConfig]) => {
      try {
        this.themeSounds[key] = new Howl({
          src: [soundConfig.src],
          volume: soundConfig.volume || 0.5,
          rate: soundConfig.rate || 1.0,
          preload: true,
          onloaderror: (id, error) => {
            console.warn(`Failed to load theme sound: ${key}`, error);
            delete this.themeSounds[key];
          }
        });
      } catch (error) {
        console.warn(`Error creating theme sound: ${key}`, error);
      }
    });

    this.loadedThemeId = this.activeThemeId;
  }

  /**
   * Unload current theme sounds and cleanup
   */
  unloadThemeSounds() {
    Object.values(this.themeSounds).forEach(sound => {
      try {
        if (sound && typeof sound.unload === 'function') {
          sound.stop();
          sound.unload();
        }
      } catch (error) {
        console.warn('Error unloading theme sound:', error);
      }
    });

    this.themeSounds = {};
    this.currentThemeConfig = null;
    this.loadedThemeId = null;
  }

  /**
   * Get theme-specific sound for a game sound key
   * Returns null if no theme sound available (caller should use default)
   *
   * @param {string} soundKey - Game sound key ('fall', 'whipSound', 'blasterSound', etc.)
   * @returns {Howl|null} Theme sound or null for fallback to default
   */
  getSound(soundKey) {
    // No theme active
    if (!this.activeThemeId || !this.currentThemeConfig) {
      return null;
    }

    // Get theme sound key from mapping
    const mappings = this.currentThemeConfig.mappings;
    if (!mappings || !mappings[soundKey]) {
      return null; // No mapping, use default
    }

    const themeSoundKey = mappings[soundKey];
    if (themeSoundKey === null) {
      return null; // Explicitly mapped to use default
    }

    // Get theme sound
    const themeSound = this.themeSounds[themeSoundKey];
    if (themeSound && themeSound.state() === 'loaded') {
      return themeSound;
    }

    return null; // Sound not loaded or failed, use default
  }

  /**
   * Play a sound with theme awareness
   * Automatically falls back to default sound if theme sound not available
   *
   * @param {string} soundKey - Game sound key
   * @param {object} options - Playback options {checkPlaying: boolean}
   */
  playSound(soundKey, options = {}) {
    const { checkPlaying = true } = options;

    // Try to get theme sound
    const themeSound = this.getSound(soundKey);
    const sound = themeSound || this.defaultSounds[soundKey];

    if (!sound) {
      return; // No sound available
    }

    // Check if already playing (prevent overlap)
    if (checkPlaying && sound.playing()) {
      return;
    }

    try {
      sound.play();
    } catch (error) {
      console.warn(`Error playing sound: ${soundKey}`, error);
    }
  }

  /**
   * Cleanup all theme sounds
   */
  cleanup() {
    this.unloadThemeSounds();
    this.activeThemeId = null;
  }

  /**
   * Check if a theme is currently active
   */
  isThemeActive() {
    return this.activeThemeId !== null;
  }

  /**
   * Get current active theme ID
   */
  getActiveThemeId() {
    return this.activeThemeId;
  }
}

export default ThemeSoundManager;
